import React, { useEffect, useState } from 'react';

import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';
import { deserialize } from 'serializr';

import last from 'lodash/last';

import { Job } from '@vromo/models';
import { Role } from '@vromo/models';
import { Chat } from '@vromo/models';
import { User } from '@vromo/models';

import format from 'date-fns/format';

import Title from '@vromo/ui/components/Title';
import ProfileImage from '@vromo/ui/components/ProfileImage';
import Modal, { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';
import Button from '@vromo/ui/components/Button';
import Tooltip from '@vromo/ui/components/Tooltip';

interface Props {
    isOpen: boolean;
    selectedJob?: Job;
    onClose: () => void;
}

function ChatMessage({ chat }: { chat: Chat }) {
    const messageDate = new Date(chat.created!);

    return (
        <Tooltip
            position="bottom"
            label={`${format(messageDate, 'MMMM Do, YYYY')} at ${format(messageDate, 'hh:mm A')}`}>
            <div
                style={{
                    backgroundColor: '#eee',
                    borderRadius: 20,
                    margin: '1px 0px',
                    paddingTop: 8,
                    paddingBottom: 8,
                    paddingLeft: 14,
                    paddingRight: 14,
                    color: '#000'
                }}>
                <p
                    style={{
                        fontSize: 14,
                        margin: 0,
                        wordBreak: 'break-word'
                    }}>
                    {chat.message}
                </p>
            </div>
        </Tooltip>
    );
}

interface ChatGroupProps {
    messages: Chat[];
    isDashboardUser: boolean;
}
function ChatGroup({ messages, isDashboardUser }: ChatGroupProps) {
    const user = messages[0]!.from;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isDashboardUser ? 'flex-start' : 'flex-end'
            }}>
            <div style={{ display: 'flex', maxWidth: '70%' }}>
                <Tooltip
                    position="bottom"
                    label={user!.contact!.name || user!.contact!.email || ''}>
                    <div style={{ marginTop: 'auto', order: isDashboardUser ? 1 : 2 }}>
                        <ProfileImage url={user?.avatar?.url} user={user!} size={32} />
                    </div>
                </Tooltip>
                <div
                    style={{
                        margin: '0px 5px 10px 5px',
                        flex: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isDashboardUser ? 'flex-start' : 'flex-end',
                        order: isDashboardUser ? 2 : 1
                    }}>
                    {messages.map((m) => (
                        <ChatMessage chat={m} key={m.created} />
                    ))}
                </div>
            </div>
        </div>
    );
}

interface ChatWindowProps {
    chatItems: Chat[];
    dashboardUsers: number[];
}

function ChatWindow({ chatItems, dashboardUsers }: ChatWindowProps) {
    function createChatGroups(chatItems: Chat[]) {
        return chatItems.reduce(
            (groups: Chat[][], chat) => {
                const lastGroup = last(groups);

                if (lastGroup?.length) {
                    const lastUserId = last(lastGroup)?.from!.id;

                    lastUserId === chat.from!.id
                        ? groups[groups.length - 1]!.push(chat)
                        : groups.push([chat]);
                } else {
                    groups[groups.length - 1]!.push(chat);
                }

                return groups;
            },
            [[]]
        );
    }

    function isDashboardUser(user: User) {
        return dashboardUsers.includes(user.id!);
    }

    const chatGroups = createChatGroups(chatItems);

    return (
        <div style={{ maxWidth: 800, overflow: 'auto' }}>
            {chatGroups.map((chatGroup) => {
                return (
                    <ChatGroup
                        key={chatGroup[0]?.created}
                        messages={chatGroup}
                        isDashboardUser={isDashboardUser(chatGroup[0]!.from!)}
                    />
                );
            })}
        </div>
    );
}

export default observer<Props>(function ChatHistoryModal({ isOpen, selectedJob, onClose }) {
    const [dashboardUserIds, setDashboardUserIds] = useState([]);

    const {
        authStore: { graph, role }
    } = useStore('rootStore');

    useEffect(() => {
        graph.root
            .graph()
            .roles(role.id)
            .account()
            .roles()
            .get()
            .then(({ items }) => {
                setDashboardUserIds(items.map((r: Role) => r.user!.id));
            });
    }, []);

    function renderChatWindow() {
        const job = deserialize(Job, selectedJob);

        return <ChatWindow chatItems={job.chat!.items} dashboardUsers={dashboardUserIds} />;
    }

    function renderEmptyContent() {
        return (
            <div
                style={{
                    height: '30vw',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                No chat history to show.
            </div>
        );
    }

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose}>
            <ModalHeader>
                <Title>Chat History</Title>
            </ModalHeader>
            <ModalContent style={{ overflow: 'unset' }}>
                {selectedJob && selectedJob.chat!.items.length
                    ? renderChatWindow()
                    : renderEmptyContent()}
            </ModalContent>
            <ModalActions>
                <Button onClick={onClose}>Close</Button>
            </ModalActions>
        </Modal>
    );
});
